import React from 'react';
import { motion } from 'framer-motion';
import { Quote, Star, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTheme } from '../../layout/ThemeContext';

const testimonials = [
  {
    quote: 'We cut our BRSR reporting time from six weeks to two days. The AI analysis surfaced insights our consultants had missed for three years.',
    name: 'Sustainability Director',
    company: 'Fortune 500 Manufacturing',
    rating: 5,
  },
  {
    quote: 'The platform paid for itself in the first quarter. We reallocated the consultant budget to actual sustainability projects instead of paperwork.',
    name: 'CFO',
    company: 'Mid-Market Financial Services',
    rating: 5,
  },
  {
    quote: 'ESGCaffe handles CSRD, GRI, and our custom board framework simultaneously. No other tool we evaluated could do that without professional services.',
    name: 'ESG Program Manager',
    company: 'European Technology Group',
    rating: 5,
  },
];

const stagger = {
  parent: {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.12, delayChildren: 0.1 } },
  },
  child: {
    hidden: { opacity: 0, y: 28 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.65, ease: [0.22, 1, 0.36, 1] } },
  },
};

const TestimonialBar = () => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  return (
    <section
      className={`py-10 md:py-12 transition-colors duration-500 ${
        isDark ? 'bg-[#0f172a]' : 'bg-white'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">

        {/* Header */}
        <motion.div
          className="text-center mb-12"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-60px' }}
          transition={{ duration: 0.65, ease: [0.22, 1, 0.36, 1] }}
        >
          <div className={`text-[0.7rem] font-bold uppercase tracking-[0.15em] mb-3 ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`}>
            Customer Stories
          </div>
          <h2
            className={`text-3xl md:text-4xl lg:text-[2.8rem] font-extrabold tracking-tight leading-tight ${
              isDark ? 'text-white' : 'text-slate-900'
            }`}
          >
            Trusted by Teams Who{' '}
            <span
              className="bg-clip-text text-transparent"
              style={{
                backgroundImage: isDark
                  ? 'linear-gradient(135deg, #34d399 0%, #22d3ee 100%)'
                  : 'linear-gradient(135deg, #059669 0%, #0891b2 100%)',
              }}
            >
              Mean Business
            </span>
          </h2>
          <p className={`mt-3 text-sm md:text-base max-w-xl mx-auto leading-relaxed ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
            Real results from sustainability leaders using ESGCaffe to move faster, report smarter, and focus on what matters.
          </p>
        </motion.div>

        {/* Cards */}
        <motion.div
          className="grid grid-cols-1 md:grid-cols-3 gap-6"
          variants={stagger.parent}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-60px' }}
        >
          {testimonials.map((t, i) => (
            <motion.div
              key={i}
              variants={stagger.child}
              className={`group relative flex flex-col rounded-2xl border p-7 transition-all duration-300 hover:shadow-xl ${
                isDark
                  ? 'border-white/[0.08] bg-white/[0.03] hover:bg-white/[0.06]'
                  : 'border-slate-200/80 bg-white shadow-sm hover:shadow-slate-200/60'
              }`}
            >
              {/* Top accent bar */}
              <div
                className="absolute top-0 left-0 right-0 h-[3px] rounded-t-2xl"
                style={{
                  background: isDark
                    ? 'linear-gradient(90deg, #34d399, #22d3ee)'
                    : 'linear-gradient(90deg, #059669, #0891b2)',
                }}
              />

              <Quote
                className={`h-7 w-7 mb-5 ${isDark ? 'text-emerald-500/40' : 'text-emerald-200'}`}
                strokeWidth={1.5}
              />

              <p className={`text-sm md:text-base leading-relaxed flex-1 italic ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>
                "{t.quote}"
              </p>

              <div className={`mt-6 pt-5 border-t ${isDark ? 'border-white/[0.07]' : 'border-slate-100'}`}>
                <div className="flex items-center gap-1 mb-2.5">
                  {Array.from({ length: t.rating }).map((_, j) => (
                    <Star key={j} className="h-3.5 w-3.5 fill-amber-400 text-amber-400" />
                  ))}
                </div>
                <div className={`text-sm font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>{t.name}</div>
                <div className={`text-xs mt-0.5 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>{t.company}</div>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* CTA */}
        <motion.div
          className="mt-10 flex justify-center"
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.35, ease: [0.22, 1, 0.36, 1] }}
        >
          <Link to="/comparison">
            <button
              className={`group inline-flex items-center gap-2.5 rounded-md border px-8 py-3.5 text-sm font-bold transition-all duration-300 ${
                isDark
                  ? 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
                  : 'border-slate-200 bg-white text-slate-700 shadow-sm hover:bg-slate-50'
              }`}
            >
              Why ESGCaffe?
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5" />
            </button>
          </Link>
        </motion.div>

      </div>
    </section>
  );
};

export default TestimonialBar;
